import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';

type LoadState = 'loading' | 'ready' | 'error';

const PRIVACY_POLICY_PATH = `${process.env.PUBLIC_URL || ''}/privacy_policy.md`;

const PrivacyPolicy: React.FC = () => {
  const [markdown, setMarkdown] = useState<string>('');
  const [state, setState] = useState<LoadState>('loading');

  useEffect(() => {
    let isMounted = true;

    const loadMarkdown = async () => {
      try {
        const response = await fetch(PRIVACY_POLICY_PATH, { cache: 'no-cache' });
        if (!response.ok) {
          throw new Error('Failed to fetch privacy policy');
        }
        const text = await response.text();
        if (isMounted) {
          setMarkdown(text);
          setState('ready');
        }
      } catch (error) {
        if (isMounted) {
          setState('error');
        }
      }
    };

    loadMarkdown();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Box component="article" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h3" component="h1">
        Privacy Policy
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Learn how R2B Platform collects, processes, and protects your personal data.
      </Typography>
      {state === 'loading' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading privacy policy…</Typography>
        </Box>
      )}
      {state === 'error' && (
        <Alert severity="error">
          We could not load the privacy policy. Please refresh the page or contact support if the problem persists.
        </Alert>
      )}
      {state === 'ready' && (
        <Typography
          component="pre"
          sx={{
            whiteSpace: 'pre-wrap',
            fontFamily: theme => theme.typography.body1.fontFamily,
            fontSize: theme => theme.typography.body1.fontSize,
            lineHeight: 1.7,
            mt: 1,
          }}
        >
          {markdown}
        </Typography>
      )}
    </Box>
  );
};

export default PrivacyPolicy;
